import { useState, lazy, Suspense } from "react";
import { Check, Plus, Minus, ArrowUpRight, Instagram, Facebook, Linkedin } from "lucide-react";
import teamSiteJpg from "@/assets/mao-de-obra-temporaria-para-construcao-civil-selectus-rh.jpg";
import teamSiteWebpAsset from "@/assets/mao-de-obra-temporaria-para-construcao-civil-selectus-rh.webp.asset.json";
import logoWebp from "@/assets/logo-selectus-rh.webp.asset.json";
const teamSiteWebp = teamSiteWebpAsset.url;
const logoSrc = logoWebp.url;
import { createContext } from "react";
import OrangeCTA from "./OrangeCTA";
import LeadForm from "./LeadForm";
import ClusterLinks, { type ClusterPage } from "./ClusterLinks";

import type { LandingPageData } from "@/lib/landing-pages.types";
import {
  mergeMatrixContent,
  renderRich,
  renderPlain,
  type InterpolateVars,
} from "@/lib/matrix-content";

const LeadFormDialog = lazy(() => import("./LeadFormDialog"));

const FormDialogContext = createContext<() => void>(() => {});

function interpolatePlaceholders(
  text: string | null | undefined,
  cidade?: string | null,
  bairro?: string | null,
  segmento?: string | null,
  prepCidade?: string | null,
  prepBairro?: string | null,
): string {
  if (!text) return "";
  const pc = (prepCidade && prepCidade.trim()) || "em";
  const pb = (prepBairro && prepBairro.trim()) || "em";
  const c = cidade ?? "São Paulo";
  const b = bairro ?? "";
  return text
    .replace(/\{no_cidade\}/g, c ? `${pc} ${c}` : "")
    .replace(/\{no_bairro\}/g, b ? `${pb} ${b}` : "")
    .replace(/\{prep_cidade\}/g, pc)
    .replace(/\{prep_bairro\}/g, pb)
    .replace(/\{cidade\}/g, c)
    .replace(/\{bairro\}/g, b)
    .replace(/\{segmento\}/g, segmento ?? "construção civil");
}

function keywordFor(
  pageData: LandingPageData | undefined,
  variant: "as-is" | "lowercase" = "as-is",
): string {
  const raw =
    pageData?.palavra_chave && pageData.palavra_chave.trim().length > 0
      ? pageData.palavra_chave
      : "Mão de obra temporária para construção civil";
  const interpolated = interpolatePlaceholders(
    raw,
    pageData?.cidade,
    pageData?.bairro,
    pageData?.matrix?.segmento ?? "construção civil",
    pageData?.preposicao_cidade,
    pageData?.preposicao_bairro,
  ).replace(/\s+/g, " ").trim();
  if (variant === "lowercase") {
    return interpolated.charAt(0).toLowerCase() + interpolated.slice(1);
  }
  return interpolated;
}

/* ---------------- Primitives ---------------- */

function Wordmark({ variant = "dark" }: { variant?: "dark" | "light" }) {
  const dark = variant === "dark";
  return (
    <a href="#top" className="flex items-center gap-2.5">
      <span
        className="inline-block h-2.5 w-2.5 rounded-full"
        style={{ background: "linear-gradient(135deg,oklch(0.72 0.2 45),oklch(0.62 0.22 30))" }}
      />
      <span
        className={`font-display text-[15px] font-bold tracking-tight ${
          dark ? "text-brand-navy" : "text-white"
        }`}
      >
        Selectus<span className="text-brand-orange">.</span>
        <span className={`ml-1.5 text-[10px] font-medium tracking-[0.24em] ${dark ? "text-muted-foreground" : "text-white/50"}`}>
          RH
        </span>
      </span>
    </a>
  );
}

function Eyebrow({ children, light = false }: { children: React.ReactNode; light?: boolean }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] ${
        light ? "text-white/60" : "text-brand-navy/60"
      }`}
    >
      <span className="h-px w-6 bg-brand-orange" />
      {children}
    </div>
  );
}


/* ---------------- Data ---------------- */

const testimonials = [
  {
    quote:
      "Precisávamos escalar o canteiro sem comprometer prazos. A Selectus entregou profissionais qualificados em 48h.",
    name: "Luiz Fernando",
    role: "Gerente de Obras · Construtora Nacional",
  },
  {
    quote:
      "Reposição rápida e equipe que entende a dinâmica da construção civil. Fez toda a diferença nas nossas frentes.",
    name: "Márcio Alves",
    role: "Head de RH · Grupo Construtor",
  },
  {
    quote:
      "Reduzimos passivo trabalhista e ganhamos previsibilidade. Parceria estratégica de verdade.",
    name: "Renata Souza",
    role: "Diretora Financeira · Construtora RS",
  },
];

/* ---------------- FAQ ---------------- */

function FaqItem({ q, a, idx }: { q: string; a: string; idx: number }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-border/70">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-6 py-5 text-left"
      >
        <span className="font-display text-base font-semibold text-brand-navy md:text-[17px]">
          {q}
        </span>
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition ${
            open ? "border-brand-orange bg-brand-orange text-white" : "border-border text-brand-navy"
          }`}
        >
          {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </span>
      </button>
      <div className={`grid transition-all ${open ? "grid-rows-[1fr] pb-5" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground md:text-[15px]">
            {a}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Page ---------------- */

export default function LandingPage({
  pageData,
  clusterPages,
}: {
  pageData?: LandingPageData;
  clusterPages?: ClusterPage[];
}) {
  const cidade = pageData?.cidade;
  const bairro = pageData?.bairro;
  const prepCidade = pageData?.preposicao_cidade ?? "em";
  const prepBairro = pageData?.preposicao_bairro ?? "em";
  const segmento = pageData?.matrix?.segmento ?? "construção civil";
  const keyword = keywordFor(pageData, "as-is");
  const keywordLower = keywordFor(pageData, "lowercase");
  const [formOpen, setFormOpen] = useState(false);
  const openForm = () => setFormOpen(true);

  const content = mergeMatrixContent(pageData?.matrix);
  const vars: InterpolateVars = {
    cidade,
    bairro,
    prepCidade,
    prepBairro,
    segmento,
    palavraChave: keyword,
    palavraChaveLower: keywordLower,
  };
  // Imagem "sobre": para a matriz Terceirização de Mão de Obra usamos uma
  // imagem dedicada, servida com nome de arquivo contendo a palavra-chave da
  // landing (SEO). Demais matrizes mantêm a imagem original.
  const isTerceirizacao =
    (pageData?.matrix?.nome ?? "").toLowerCase().includes("terceiriza");
  const aboutFilename = pageData?.slug
    ? `sobre-${pageData.slug}.webp`
    : "sobre-terceirizacao-de-mao-de-obra.webp";
  const aboutImgWebp = isTerceirizacao
    ? `/about-image/${aboutFilename}`
    : teamSiteWebp;
  const aboutImgFallback = isTerceirizacao ? aboutImgWebp : teamSiteJpg;
  const aboutImgAlt = isTerceirizacao
    ? `Equipe Selectus RH — ${keywordLower}`
    : `Equipe de pedreiros e serventes da Selectus RH — ${keywordLower}`;
  return (
    <FormDialogContext.Provider value={openForm}>
    <main id="top" className="min-h-screen bg-background text-foreground">
      {/* ---------- NAV ---------- */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <a href="#top" aria-label="Selectus RH - Página inicial">
            <picture>
              <source type="image/webp" srcSet={logoWebp.url} />
              <img
                src={logoSrc}
                alt={`Selectus RH - ${keyword}`}
                width="160"
                height="40"
                className="h-10 w-auto"
                fetchPriority="high"
                decoding="async"
              />
            </picture>
          </a>
          <nav className="hidden items-center gap-8 md:flex">
            <a href="#beneficios" className="text-sm text-muted-foreground transition hover:text-brand-navy">
              Benefícios
            </a>
            <a href="#processo" className="text-sm text-muted-foreground transition hover:text-brand-navy">
              Processo
            </a>
            <a href="#sobre" className="text-sm text-muted-foreground transition hover:text-brand-navy">
              Sobre
            </a>
            <a href="#faq" className="text-sm text-muted-foreground transition hover:text-brand-navy">
              FAQ
            </a>
          </nav>
          <button
            type="button"
            onClick={openForm}
            className="inline-flex items-center gap-2 rounded-full border border-brand-navy/15 bg-white px-4 py-2 text-xs font-semibold tracking-wide text-brand-navy transition hover:border-brand-orange hover:text-brand-orange"
          >
            {renderPlain(content.hero_button, vars)}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </header>

      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-6xl gap-12 px-6 pb-14 pt-8 lg:grid-cols-[1.15fr_1fr] lg:gap-16 lg:pt-10">
          {/* Left */}
          <div className="flex flex-col justify-center">
            <Eyebrow>{renderPlain(content.hero_eyebrow, vars)}</Eyebrow>
            <h1 className="mt-6 font-display text-[44px] font-bold leading-[1.02] tracking-[-0.03em] text-brand-navy md:text-[64px] lg:text-[72px]">
              {renderRich(content.hero_title, vars)}
            </h1>
            <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-muted-foreground">
              {renderPlain(pageData?.texto_hero ?? content.hero_subtitle, vars)}
            </p>

            <div className="mt-10 grid grid-cols-3 gap-8 border-t border-border/70 pt-8">
              {[
                { k: "20+", v: "anos de mercado" },
                { k: "2K+", v: "profissionais ativos" },
                { k: "7 dias", v: "para mobilizar" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-display text-3xl font-bold tracking-tight text-brand-navy md:text-4xl">
                    {s.k}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <LeadForm />
          </div>
        </div>

        {/* Roles strip */}
        <div className="overflow-hidden border-y border-border/60 bg-muted/30 py-6">
          <div className="flex w-max animate-marquee whitespace-nowrap font-display text-xl font-semibold uppercase tracking-[0.14em] text-brand-navy md:text-3xl">
            {Array.from({ length: 2 }).map((_, dup) => (
              <div key={dup} className="flex items-center pr-10" aria-hidden={dup === 1}>
                {content.profissoes.map((r) => (
                  <span key={r} className="flex items-center">
                    <span>{r}</span>
                    <span className="mx-8 text-brand-orange">•</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
        {content.aviso && content.aviso.trim().length > 0 && (
          <div className="border-b border-amber-200 bg-amber-50 py-3">
            <div className="mx-auto flex max-w-6xl items-center justify-center gap-3 px-6 text-center">
              <span aria-hidden className="text-amber-600">⚠</span>
              <p className="text-sm leading-relaxed text-amber-900">
                {renderRich(content.aviso, vars, "font-semibold text-amber-900")}
              </p>
            </div>
          </div>
        )}
      </section>

      {/* ---------- BENEFITS ---------- */}
      <section id="beneficios" className="bg-muted/30 py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-10 md:grid-cols-[1fr_1.4fr] md:gap-16">
            <div>
              <Eyebrow>{renderPlain(content.benefits_eyebrow, vars)}</Eyebrow>
              <h2 className="mt-5 font-display text-3xl font-bold leading-tight tracking-tight text-brand-navy md:text-[44px]">
                {renderRich(content.benefits_title, vars)}
              </h2>
              <p className="mt-5 max-w-md text-[15px] leading-relaxed text-muted-foreground">
                {renderRich(content.benefits_intro, vars, "font-semibold text-brand-navy")}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2">
              {content.benefits.map((b, i) => (
                <div key={b.title} className="group">
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-xs font-semibold tracking-wider text-brand-orange">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="h-px flex-1 bg-border/70 transition group-hover:bg-brand-orange" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold tracking-tight text-brand-navy">
                    {renderPlain(b.title, vars)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {renderPlain(b.desc, vars)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- PROCESS ---------- */}
      <section id="processo" className="bg-brand-navy py-24 text-white md:py-32">
        <div className="mx-auto max-w-6xl px-6">
          <div className="max-w-2xl">
            <Eyebrow light>{renderPlain(content.process_eyebrow, vars)}</Eyebrow>
            <h2 className="mt-5 font-display text-3xl font-bold leading-tight tracking-tight md:text-[44px]">
              {renderRich(content.process_title, vars)}
            </h2>
          </div>

          <div className="mt-16 grid gap-px overflow-hidden rounded-2xl bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {content.process_steps.map((s, i) => (
              <div key={i} className="bg-brand-navy p-8 transition hover:bg-brand-navy-deep">
                <div
                  className="font-display text-5xl font-bold"
                  style={{
                    background:
                      "linear-gradient(135deg,oklch(0.75 0.19 55),oklch(0.62 0.22 30))",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-6 font-display text-lg font-semibold">
                  {renderPlain(s.title, vars)}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-white/60">
                  {renderPlain(s.desc, vars)}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap items-center gap-4">
            <OrangeCTA onClick={openForm}>{renderPlain(content.cta_mid_button, vars)}</OrangeCTA>
            <a href="#faq" className="text-sm text-white/70 transition hover:text-white">
              Tenho dúvidas →
            </a>
          </div>
        </div>
      </section>

      {/* ---------- ABOUT ---------- */}
      <section id="sobre" className="bg-muted/30 py-16 md:py-20">
        <div className="mx-auto grid max-w-6xl gap-14 px-6 md:grid-cols-2 md:items-center md:gap-20">
          <div className="relative overflow-hidden rounded-2xl">
            <picture>
            <source type="image/webp" srcSet={aboutImgWebp} />
            <img
              src={aboutImgFallback}
              alt={aboutImgAlt}
              width="800"
              height="1000"
              loading="lazy"
              decoding="async"
              sizes="(min-width: 768px) 500px, 90vw"
              className="aspect-[4/5] w-full object-cover"
            />
            </picture>
            <div className="absolute bottom-6 left-6 rounded-xl bg-white/95 px-5 py-4 backdrop-blur">
              <div className="font-display text-2xl font-bold text-brand-navy">+2.000</div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                profissionais ativos
              </div>
            </div>
          </div>

          <div>
            <Eyebrow>{renderPlain(content.about_eyebrow, vars)}</Eyebrow>
            <h2 className="mt-5 font-display text-3xl font-bold leading-tight tracking-tight text-brand-navy md:text-[42px]">
              {renderRich(content.about_title, vars, "font-semibold")}
            </h2>
            <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-muted-foreground">
              <p>{renderRich(content.about_p1, vars, "font-semibold text-brand-navy")}</p>
              <p>{renderRich(content.about_p2, vars, "font-semibold text-brand-navy")}</p>
            </div>

            <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[
                "Contratos em conformidade com a Lei 6.019/74",
                "Assessoria jurídica trabalhista dedicada",
                "Cobertura em toda Grande São Paulo",
                "Reposição garantida em até 48h",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-brand-navy">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-orange" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ---------- TESTIMONIALS ---------- */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="max-w-2xl">
            <Eyebrow>Clientes</Eyebrow>
            <h2 className="mt-5 font-display text-3xl font-bold leading-tight tracking-tight text-brand-navy md:text-[44px]">
              {renderPlain(content.testimonials_title, vars)
                .split("\n")
                .map((line, i, arr) => (
                  <span key={i}>
                    {line}
                    {i < arr.length - 1 && <br />}
                  </span>
                ))}
            </h2>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure
                key={t.name}
                className="flex flex-col rounded-2xl border border-border/70 bg-white p-7"
              >
                <div className="font-display text-4xl leading-none text-brand-orange">"</div>
                <blockquote className="mt-2 flex-1 text-[15px] leading-relaxed text-brand-navy">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-6 border-t border-border/70 pt-4">
                  <div className="font-display text-sm font-semibold text-brand-navy">{t.name}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{t.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section id="faq" className="bg-muted/30 py-16 md:py-20">
        <div className="mx-auto grid max-w-6xl gap-14 px-6 md:grid-cols-[1fr_1.6fr] md:gap-20">
          <div>
            <Eyebrow>{renderPlain(content.faq_eyebrow, vars)}</Eyebrow>
            <h2 className="mt-5 font-display text-3xl font-bold leading-tight tracking-tight text-brand-navy md:text-[42px]">
              {renderRich(content.faq_title, vars)}
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground">
              {renderPlain(content.faq_intro, vars)}
            </p>
            <div className="mt-8">
              <OrangeCTA onClick={openForm}>{renderPlain(content.hero_button, vars)}</OrangeCTA>
            </div>
          </div>

          <div>
            {content.faq.map((f, i) => (
              <FaqItem
                key={i}
                q={renderPlain(f.q, vars)}
                a={renderPlain(f.a, vars)}
                idx={i}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ---------- SEO CONTENT ---------- */}
      <section className="py-10 md:py-14">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-brand-navy md:text-[42px]">
            {renderRich(content.seo_title, vars, "font-semibold")}
          </h2>
          <div className="mt-8 space-y-5 text-[15px] leading-relaxed text-muted-foreground">
            <p>{renderRich(content.seo_p1, vars, "font-semibold text-brand-navy")}</p>
            <p>{renderPlain(pageData?.texto_beneficios ?? content.seo_p2, vars)}</p>
            <p>{renderPlain(content.seo_p3, vars)}</p>
          </div>
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <section className="relative overflow-hidden bg-brand-navy py-24 text-white md:py-28">
        <div className="pointer-events-none absolute inset-0 grid-pattern opacity-40" />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <Eyebrow light>{renderPlain(content.cta_final_eyebrow, vars)}</Eyebrow>
          <h2 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-[-0.02em] md:text-[56px]">
            {renderRich(content.cta_final_title, vars)}
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-[15px] leading-relaxed text-white/70">
            {renderPlain(content.cta_final_subtitle, vars)}
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <OrangeCTA onClick={openForm}>{renderPlain(content.cta_final_button, vars)}</OrangeCTA>
          </div>
        </div>
      </section>

      {/* ---------- FOOTER ---------- */}
      {clusterPages && clusterPages.length > 0 && (
        <ClusterLinks
          pages={clusterPages}
          currentCidade={cidade ?? null}
          segmento={segmento}
        />
      )}
      <footer className="border-t border-border/70 bg-background">
        <div className="mx-auto max-w-6xl px-6 py-14">
          <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr] md:gap-14">
            {/* Brand */}
            <div>
              <picture>
                <source type="image/webp" srcSet={logoWebp.url} />
                <img
                  src={logoSrc}
                  alt={`Selectus RH - Terceirização — ${keywordLower}`}
                  width="176"
                  height="44"
                  loading="lazy"
                  decoding="async"
                  className="h-11 w-auto"
                />
              </picture>
              <p className="mt-5 max-w-sm text-sm leading-relaxed text-muted-foreground">
                Selectus RH — <strong>{keyword}</strong> com agilidade,
                segurança jurídica e gestão completa da equipe.
              </p>
              <div className="mt-6 flex items-center gap-3">
                {[
                  { href: "https://www.instagram.com/rhselectus/", label: "Instagram", Icon: Instagram },
                  { href: "https://www.facebook.com/rhselectus/", label: "Facebook", Icon: Facebook },
                  { href: "https://www.linkedin.com/company/rhselectus", label: "LinkedIn", Icon: Linkedin },
                ].map(({ href, label, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-border/70 text-brand-navy transition hover:border-brand-orange hover:bg-brand-orange hover:text-white"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div>
              <div className="font-display text-xs font-semibold uppercase tracking-[0.24em] text-brand-navy">
                Navegação
              </div>
              <ul className="mt-5 space-y-3 text-sm text-muted-foreground">
                <li><a href="#beneficios" className="transition hover:text-brand-orange">Benefícios</a></li>
                <li><a href="#processo" className="transition hover:text-brand-orange">Processo</a></li>
                <li><a href="#sobre" className="transition hover:text-brand-orange">Sobre</a></li>
                <li><a href="#faq" className="transition hover:text-brand-orange">FAQ</a></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <div className="font-display text-xs font-semibold uppercase tracking-[0.24em] text-brand-navy">
                Atendimento
              </div>
              <ul className="mt-5 space-y-3 text-sm text-muted-foreground">
                <li>São Paulo/SP - Matriz</li>
                <li>Atendimento em todo o Brasil</li>
                <li className="text-muted-foreground">
                  Av. Paulista, 1471, Cj. 511 — Bela Vista, São Paulo/SP
                </li>
                <li className="text-muted-foreground">
                  CNPJ: 62.160.724/0001-50
                </li>
                <li>
                  <button
                    type="button"
                    onClick={openForm}
                    className="font-semibold text-brand-navy transition hover:text-brand-orange"
                  >
                    Falar com consultor →
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-border/70 pt-6 text-xs text-muted-foreground md:flex-row md:items-center">
            <div>© {new Date().getFullYear()} Selectus RH. Todos os direitos reservados.</div>
            <div className="uppercase tracking-[0.2em]">
              <strong>{keyword}</strong>
            </div>
          </div>
        </div>
      </footer>
    </main>
    {formOpen && (
      <Suspense fallback={null}>
        <LeadFormDialog open={formOpen} onOpenChange={setFormOpen} />
      </Suspense>
    )}
    </FormDialogContext.Provider>
  );
}